import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { faBan, faCheck, faInfoCircle, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import Lightbox from 'react-image-lightbox'
import NumberFormat from 'react-number-format'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Alert, Button, Card, CardBody, CardFooter, Col, DropdownItem, DropdownMenu, DropdownToggle, Media, Row, UncontrolledDropdown } from 'reactstrap'
import { changeAdvanceStatus, findAllAdvancePayments } from '../../helpers/api/finance'
import { SUCCESS_RESPONSE } from '../../helpers/codeResponses/codes'
import handleErrorMessage from '../../helpers/errors/handleErrorMessage'
import withRedirect from '../../helpers/hoc/withRedirect'
import useSWRFetchPagination from '../../helpers/hooks/useSWRFetchPagination'
import useModuleBase from '../../helpers/hooks/useModuleBase'
import ButtonIcon from '../common/ButtonIcon'
import Calendar from '../common/Calendar'
import FalconCardHeader from '../common/FalconCardHeader'
import Filter from '../common/Filter'
import PageHeader from '../common/PageHeader'
import FullPagination from '../common/pagination/FullPagination'
import SupportLink from '../root/support/components/SupportLink'
import { isIterableArray, Message, Or, PreloadState } from '../UI/preloading/PreloadState'
import { Toast } from '../UI/toast/Toast'
import VoidPaymentModal from './VoidPaymentModal'
import { CurrencyContext } from '../../context/Context'
import { formatAmountForBackend } from '../../helpers/currencyConversion'

const Advance = ({ setRedirectUrl, setRedirect }) => {

    const formatDateSafe = (date) => {
        if (!date) return 'N/D';

        const raw = String(date).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            const [year, month, day] = raw.split('-');
            return `${year}-${month}-${day}`;
        }

        const parsed = moment(raw);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : 'N/D';
    };

    const moduleBase = useModuleBase();
    const { selectedCondominium } = useSelector(state => state.condominium);
    const { user: { role, token } } = useSelector(state => state.auth);
    const { baseCurrency } = useContext(CurrencyContext);
    const [images, setImages] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [index, setIndex] = useState(0);
    const [params, setParams] = useState({
        status: null,
        condominiumId: selectedCondominium
    });
    const [voidTarget, setVoidTarget] = useState(null);
    const {
        data,
        error,
        isLoading,
        meta,
        mutate,
        handler: { nextPage, previousPage, perPage, goToPage }
    } = useSWRFetchPagination(findAllAdvancePayments, params);

    useEffect(() => {
        setImages(data.map(({ voucher }) => `${import.meta.env.VITE_URL}/view/image/${token}?path=${voucher}&size=large`));
        // eslint-disable-next-line
    }, [data])

    const approve = async (advancePayment, paymentObj) => {
        try {
            const targetPayment = paymentObj || (typeof advancePayment === 'object' ? advancePayment : null);
            const advancePaymentId = typeof advancePayment === 'object' ? advancePayment?.advance_payment_id : advancePayment;

            const rawAmountConverted = targetPayment?.amount_converted ?? (
                Number.isFinite(Number(targetPayment?.amount)) && Number.isFinite(Number(targetPayment?.exchange_rate?.value))
                    ? Number(targetPayment.amount) * Number(targetPayment.exchange_rate.value)
                    : null
            );

            const payload = {
                status: 'approved',
            };

            if (rawAmountConverted !== null && rawAmountConverted !== undefined && rawAmountConverted !== '') {
                payload.amount_converted = formatAmountForBackend(rawAmountConverted);
            }

            const { code } = await changeAdvanceStatus(advancePaymentId, payload);
            if (code === SUCCESS_RESPONSE.SUCCESFUL) {
                setRedirectUrl(`/dashboard/${moduleBase}/advance`)
                setRedirect(true);
                Toast.fire({ type: 'success', title: `Aprobado exitosamente.` });
            }
        } catch (error) {
            handleErrorMessage(error);
        }
    }

    const refuse = async (advancePayment) => {
        try {
            const { code } = await changeAdvanceStatus(advancePayment, { status: 'refused' });
            if (code === SUCCESS_RESPONSE.SUCCESFUL) {
                setRedirectUrl(`/dashboard/${moduleBase}/advance`)
                setRedirect(true);
                Toast.fire({
                    type: 'success',
                    title: `Rechazado exitosamente.`,
                });
            }
        } catch (error) {
            handleErrorMessage(error);
        }
    }

    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    return <>
        <VoidPaymentModal
            isOpen={voidTarget !== null}
            toggle={() => setVoidTarget(null)}
            kind='advance'
            paymentId={voidTarget}
            onVoided={() => mutate()}
        />
        <PageHeader
            title={role === 'property' ? "Mis saldos a favor" : 'Saldos a favor de propietarios'} titleTag="h2"
            description={role === 'property' ? 'Visualiza tus saldos a favor en el condominio.' : 'Visualiza los saldos a favor de los propietarios en el condominio.'}
            className="mb-3" col={{ lg: 9 }}
        >
            {role === 'manager' && <SupportLink />}
        </PageHeader>
        <Card>
            <FalconCardHeader title='Saldos a favor registrados'>
                <Row>
                    <Col className='d-flex mb-2 mb-md-0'>
                        <Filter keyField='status' options={[
                            { value: 0, displayName: 'Todos', type: 'status' },
                            { value: 'approved', displayName: 'Aprobados', type: 'status' },
                            { value: 'pending', displayName: 'Pendientes', type: 'status' },
                            { value: 'refused', displayName: 'Rechazados', type: 'status' },
                            { value: 'voided', displayName: 'Anulados', type: 'status' },
                        ]}
                            selectType={setParams}
                            dateFilter={false}
                        />
                        <ButtonIcon icon={faPlus} color='falcon-default' tag={Link} to='/dashboard/my/finance/advance/add'>Añadir saldo a favor</ButtonIcon>
                    </Col>
                </Row>
            </FalconCardHeader>
            <CardBody className="fs--1 pt-0">
                <PreloadState value={false} cond={isIterableArray(data)} loading={isLoading}>
                    <Or value={true}>
                        <>
                            {data.map((payment, i) => {
                                const date = new Date(payment.created_at);
                                const paymentDate = formatDateSafe(payment.payment_date);
                                const convertedAmount = Number(payment?.amount_converted);
                                return (
                                    <Row key={i} className={classNames({ 'border-top py-3': payment.status === 'pending', 'alert-warning border-top py-3': payment.status === 'refused', 'alert-success border-top py-3': payment.status === 'approved', 'alert-secondary border-top py-3': payment.status === 'voided' })}>
                                        <Col className='d-flex align-items-center'>
                                            <Media>
                                                <Calendar month={months[date.getMonth()]} day={date.getDate()} />
                                                <Media body className="position-relative pl-3 align-self-center">
                                                    <p className='mb-0'>Saldo a favor</p>
                                                    <p className='fs--1 mb-0 font-weight-bolder'>{payment?.status === 'pending' ? 'Pendiente' : payment?.status === 'canceled' ? 'Cancelado por ti' : payment.status === 'refused' ? 'Rechazado' : payment.status === 'voided' ? 'Anulado' : 'Aprobado'}</p>
                                                    {payment.status === 'voided' && payment.void_reason && <p className='fs--2 mb-0 text-600'>Motivo: {payment.void_reason}</p>}
                                                </Media>
                                            </Media>
                                            <Media>
                                                <Media body className='pl-3 align-self-center'>
                                                    <p className='mb-0'>{payment.reference ? `Referencia: ${payment.reference}` : 'No adjuntada'}</p>
                                                    <p className='mb-0'><Button color='link' className='fs--1 mb-0 font-weight-bolder p-0' onClick={() => { setIsOpen(true); setIndex(i) }}>Ver comprobante</Button>
                                                        {isOpen &&
                                                            <Lightbox
                                                                reactModalStyle={{ zIndex: 1250 }}
                                                                mainSrc={images[index]}
                                                                onCloseRequest={() => setIsOpen(false)}
                                                            />
                                                        }
                                                    </p>
                                                </Media>
                                            </Media>
                                        </Col>
                                        <Col xs={2} style={{ overflowX: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Media>
                                                <Media body className="border-left position-relative pl-3">
                                                    <p className='mb-0 font-weight-bolder'>Fecha de pago</p>
                                                    <p className="fs--1 mb-0 text-600">
                                                        {paymentDate}
                                                    </p>
                                                </Media>
                                            </Media>
                                        </Col>
                                        <Col xs={3} style={{ overflowX: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Media>
                                                <Media body className="border-left position-relative pl-3">
                                                    <p className="mb-0 text-900 font-weight-bold">
                                                        <NumberFormat displayType='text' value={payment?.amount} thousandSeparator decimalScale={2} fixedDecimalScale prefix={`${baseCurrency.symbol} `} />
                                                    </p>
                                                    {Number.isFinite(convertedAmount) && (
                                                        <p className="fs--1 mb-0 text-600 mt-1">
                                                            <NumberFormat
                                                                displayType='text'
                                                                value={convertedAmount}
                                                                thousandSeparator
                                                                decimalScale={2}
                                                                fixedDecimalScale
                                                                prefix={`${payment?.currency?.iso || payment?.exchange_rate?.currency?.iso || ''} `}
                                                            />
                                                        </p>
                                                    )}
                                                </Media>
                                            </Media>
                                        </Col>
                                        {role === 'manager' &&
                                            <Col xs={'auto'} className="d-sm-flex align-items-center">
                                                <UncontrolledDropdown direction="left" className="d-inline-block ml-2">
                                                    <DropdownToggle color="falcon-default" size="sm">
                                                        <FontAwesomeIcon icon="ellipsis-h" />
                                                    </DropdownToggle>
                                                    <DropdownMenu right>
                                                        {payment.status === 'pending' && <DropdownItem className='d-flex align-items-center justify-content-between' onClick={() => approve(payment.advance_payment_id, payment)}><FontAwesomeIcon className='text-success' icon={faCheck} />Aprobar saldo a favor</DropdownItem>}
                                                        {payment.status === 'pending' && <DropdownItem className='d-flex align-items-center justify-content-between' onClick={() => refuse(payment.advance_payment_id)}><FontAwesomeIcon className='text-danger' icon={faTimes} />Rechazar saldo a favor</DropdownItem>}
                                                        {payment.status === 'approved' && <DropdownItem className='d-flex align-items-center justify-content-between' onClick={() => setVoidTarget(payment.advance_payment_id)}><FontAwesomeIcon className='text-danger' icon={faBan} />Anular saldo a favor</DropdownItem>}
                                                    </DropdownMenu>
                                                </UncontrolledDropdown>
                                            </Col>}
                                    </Row>
                                )
                            })}
                        </>
                    </Or>
                    <Or value={false} cond={!!error}>
                        <Message value={false} color={'info'} className='p-3' align='left' icon={faInfoCircle}>
                            <>
                                Aún no hay saldos a favor para mostrar.
                                Puedes registrar uno ahora mismo pulsando el siguiente enlace
                                <Button data-testid="Button" tag={Link} color="link" size="sm" className="ml-1 pl-0" to={`/dashboard/my/finance/advance/add`}>
                                    Añadir saldo a favor
                                </Button>
                            </>
                        </Message>
                        <Or value={true}>
                            <Alert color="danger" className="mb-0">
                                Esta página no está disponible
                                <Button data-testid="Button" color="link" size="sm" className="ml-1 pl-0" onClick={() => { setRedirectUrl(`/dashboard/banks`); setRedirect(true) }}>
                                    Volver
                                </Button>
                            </Alert>
                        </Or>
                    </Or>
                </PreloadState>
            </CardBody>
            <CardFooter>
                {isIterableArray(data) &&
                    <FullPagination
                        nextPage={nextPage}
                        prevPage={previousPage}
                        goToPage={goToPage}
                        meta={meta}
                        className={'shadow-none'}
                        posText={"pagos"}
                        perPage={perPage} />
                }
            </CardFooter>
        </Card>
    </>
}

export default withRedirect(Advance);